import { Globe, Check, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LANGUAGES, useLanguage } from "@/lib/language";

/**
 * Language picker. Content is machine-translated on the page; English stays
 * the original. URLs never change.
 */
export function LanguageSelector({
  className,
  variant = "compact",
}: {
  className?: string;
  variant?: "compact" | "full";
}) {
  const { lang, setLang, translating } = useLanguage();
  const active = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Language: ${active.label}. Change language`}
        data-no-translate
        className={
          className ??
          "inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/30 px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--gold-deep)] hover:bg-gold/10 md:px-3 md:text-xs"
        }
      >
        {translating ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
        ) : (
          <Globe className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
        )}
        <span aria-hidden className="hidden sm:inline">
          {active.flag}
        </span>
        <span>{variant === "full" ? active.label : active.short}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[60] w-56" data-no-translate>
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Language
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onSelect={() => setLang(l.code)}
            className="flex cursor-pointer items-center gap-2 text-sm"
          >
            <span aria-hidden>{l.flag}</span>
            <span className="flex-1">{l.label}</span>
            {l.code === "en" && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Original
              </span>
            )}
            {l.code === lang && <Check className="h-4 w-4 text-[var(--gold-deep)]" aria-hidden />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">
          Automatic translation. English is the original. Your own journal entries, notes and posts
          are never translated.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
